import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, ROLE_GROUPS, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/layout/AppShell";
import { Toaster } from "./components/ui/sonner";
import InstallAppButton from "./components/InstallAppButton";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import POS from "./pages/POS";
import Tickets from "./pages/Tickets";
import Users from "./pages/Users";
import Branches from "./pages/Branches";
import Operators from "./pages/Operators";
import Games from "./pages/Games";
import PlayTypes from "./pages/PlayTypes";
import NumberLimits from "./pages/NumberLimits";
import Results from "./pages/Results";
import Payouts from "./pages/Payouts";
import Reports from "./pages/Reports";
import CashSessions from "./pages/CashSessions";
import AuditLogs from "./pages/AuditLogs";

import Settings from "./pages/Settings";
import Sessions from "./pages/Sessions";
import AuthorizedDevices from "./pages/AuthorizedDevices";
import PublicTicket from "./pages/PublicTicket";
import Consortiums from "./pages/Consortiums";

const { MANAGE, SUPERVISE, AUDIT, OPS, ALL } = ROLE_GROUPS;

function Shell({ children, roles }) {
  return (
    <ProtectedRoute roles={roles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function FullscreenShell({ children, roles }) {
  return (
    <ProtectedRoute roles={roles}>
      {children}
    </ProtectedRoute>
  );
}

function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === "cashier") return <Navigate to="/pos" replace />;
  return <Shell roles={ALL}><Dashboard /></Shell>;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/t/:ticketNumber" element={<PublicTicket />} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/pos" element={<FullscreenShell roles={OPS}><POS /></FullscreenShell>} />
            <Route path="/payouts" element={<Shell roles={OPS}><Payouts /></Shell>} />
            <Route path="/results" element={<Shell roles={SUPERVISE}><Results /></Shell>} />
            <Route path="/reports" element={<Shell roles={AUDIT}><Reports /></Shell>} />
            <Route path="/cash-sessions" element={<Shell roles={OPS}><CashSessions /></Shell>} />
            <Route path="/audit-logs" element={<Shell roles={AUDIT}><AuditLogs /></Shell>} />
            <Route path="/tickets" element={<Shell roles={OPS.concat(["auditor"])}><Tickets /></Shell>} />
            <Route path="/limits" element={<Shell roles={SUPERVISE}><NumberLimits /></Shell>} />
            <Route path="/users" element={<Shell roles={MANAGE}><Users /></Shell>} />
            <Route path="/branches" element={<Shell roles={MANAGE}><Branches /></Shell>} />
            <Route path="/operators" element={<Shell roles={MANAGE}><Operators /></Shell>} />
            <Route path="/games" element={<Shell roles={MANAGE}><Games /></Shell>} />
            <Route path="/play-types" element={<Shell roles={MANAGE}><PlayTypes /></Shell>} />
            <Route path="/settings" element={<Shell roles={MANAGE}><Settings /></Shell>} />
            <Route path="/sessions" element={<Shell roles={ALL}><Sessions /></Shell>} />
            <Route path="/authorized-devices" element={<Shell roles={MANAGE}><AuthorizedDevices /></Shell>} />
            <Route path="/consortiums" element={<Shell roles={["super_admin"]}><Consortiums /></Shell>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
          <InstallAppButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
